var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import template from '@jsep-plugin/template';
import { t } from '../translate';
import { eval as evaluateExpression } from 'expression-eval';
import jsep from 'jsep';
import { DominoBuilderError, DominoBuilderErrorScope, DominoBuilderErrorSeverity, DominoBuilderErrorType, filterErrors, } from '../errors';
import { getQualifiedSymbolId, isPrimitiveType } from '../symbols';
import { ExpressionMemberType, PrimitiveTypes, } from '../types';
import { argsToString, isExpressionSyntaxWrapped, isReferenceOnly, isTemplateLiteral, parseAsJsepExpression, stripExpressionSyntax, } from './helpers';
jsep.plugins.register(template);
export class DominoBuilderExpression {
    constructor(expectedType, initialValue) {
        this.errors = [];
        this._expectedType = expectedType;
        this._initialValue = initialValue;
        this.members = [];
    }
    _loadMembers(members) {
        this.members = members;
    }
    getErrorAggregate(scope, severity) {
        return filterErrors(scope, severity, this.errors).concat(this.members.reduce((c, exp) => { var _a, _b; return c.concat((_b = (_a = exp.getErrorAggregate) === null || _a === void 0 ? void 0 : _a.call(exp, scope, severity)) !== null && _b !== void 0 ? _b : []); }, []));
    }
    deconstruct(isSubExpression, retainErrors) {
        let val = '';
        if (this.members.length === 1 && typeof this.members[0].value === 'boolean') {
            return this.members[0].value;
        }
        this.members.map(m => {
            switch (m.type) {
                case ExpressionMemberType.literal:
                    val += m.value;
                    break;
                case ExpressionMemberType.symbol:
                    const sym = getQualifiedSymbolId(m.value);
                    val += isSubExpression ? sym : '${' + sym + '}';
                    break;
                case ExpressionMemberType.expression:
                    val += '${' + m.value.deconstruct(true, retainErrors) + '}';
                    break;
                case ExpressionMemberType.operator:
                    val += ` ${m.value} `;
                    break;
                case ExpressionMemberType.call:
                    val += `${m.value}(${argsToString(m.args)})`;
                    break;
                case ExpressionMemberType.entity:
                    let v = m.value;
                    if (!retainErrors && typeof v === 'object' && !!v) {
                        v = Object.assign({}, m.value);
                        if (!!v.errors) {
                            delete v.errors;
                        }
                    }
                    val = v;
            }
        });
        return val;
    }
    construct(scope, lineNumber) {
        var _a, _b, _c, _d, _e;
        return __awaiter(this, void 0, void 0, function* () {
            this.members = [];
            let value = this._initialValue; // ?? this probably needs to be derived / reset from toString ...
            const isEntityConfiguredWithVariable = !isPrimitiveType(this._expectedType) &&
                isExpressionSyntaxWrapped(this._initialValue);
            if (isPrimitiveType(this._expectedType) || isEntityConfiguredWithVariable) {
                if (this._initialValue === null) {
                    this.members.push({
                        type: ExpressionMemberType.literal,
                        value: '',
                    });
                }
                else if (this._expectedType === PrimitiveTypes.boolean) {
                    this.members.push({
                        type: ExpressionMemberType.literal,
                        value,
                    });
                }
                else if (this._expectedType === PrimitiveTypes.string ||
                    this._expectedType === PrimitiveTypes.Url) {
                    // always parse string values as template literals
                    const parsedExp = parseAsJsepExpression(isTemplateLiteral(value) ? value : `\`${value}\``);
                    parsedExp.quasis.forEach((q, i) => {
                        this.members.push({
                            type: ExpressionMemberType.literal,
                            value: q.value.raw,
                        });
                        if (!q.tail) {
                            this._constructMembers(parsedExp.expressions[i], scope, lineNumber, this.errors, this.members);
                        }
                    });
                }
                else {
                    const parsedExp = parseAsJsepExpression(stripExpressionSyntax(value));
                    this._constructMembers(parsedExp, scope, lineNumber, this.errors, this.members);
                }
            }
            else {
                this.members.push({
                    type: ExpressionMemberType.entity,
                    value,
                    // attempt to surface any errors that exist within the entity value itself
                    getErrorAggregate: () => { var _a; return (_a = value === null || value === void 0 ? void 0 : value.errors) !== null && _a !== void 0 ? _a : []; },
                });
            }
            // remove outer expression member from non-strings
            if (this._expectedType !== PrimitiveTypes.string &&
                ((_a = this.members[0]) === null || _a === void 0 ? void 0 : _a.type) === ExpressionMemberType.expression) {
                this.members = this.members[0].value.members;
            }
            // remove empty first member, if it exists
            if (((_b = this.members[0]) === null || _b === void 0 ? void 0 : _b.type) === ExpressionMemberType.literal &&
                ((_c = this.members[0]) === null || _c === void 0 ? void 0 : _c.value) === '') {
                this.members.shift();
            }
            // remove empty last member, if it exists
            if (((_d = this.members[this.members.length - 1]) === null || _d === void 0 ? void 0 : _d.type) ===
                ExpressionMemberType.literal &&
                ((_e = this.members[this.members.length - 1]) === null || _e === void 0 ? void 0 : _e.value) === '') {
                this.members.pop();
            }
        });
    }
    _constructMembers(expr, scope, lineNumber, errors, consumedMembers, isSubExpression = false) {
        if (isReferenceOnly(expr)) {
            // Add a symbol to the members list and return
            this._constructReferenceExpressionMembers(expr, scope, lineNumber, errors, consumedMembers);
        }
        else if (expr.type === 'Literal') {
            // Add the literal to the members list and return
            consumedMembers.push({
                type: ExpressionMemberType.literal,
                value: expr.raw,
            });
        }
        else if (expr.type === 'BinaryExpression') {
            // Add the binary expression to the members list and return
            this._constructBinaryExpressionMembers(expr, scope, lineNumber, errors, consumedMembers);
        }
        else if (expr.type === 'CallExpression') {
            this._constructCallExpressionMembers(expr, scope, lineNumber, errors, consumedMembers, isSubExpression);
        }
    }
    _constructReferenceExpressionMembers(expr, scope, lineNumber, errors, consumedMembers) {
        var _a, _b;
        try {
            const symbol = evaluateExpression(expr, scope);
            const deproxiedSym = symbol.__deproxy();
            if (deproxiedSym.lineNumber >= lineNumber) {
                errors.push(new DominoBuilderError(DominoBuilderErrorType.SymbolReferencedBeforeDeclaration, DominoBuilderErrorScope.DESIGN, DominoBuilderErrorSeverity.ERROR, t('errors.symbol_referenced_before_declaration', {
                    symbolName: (_b = (_a = deproxiedSym.label) !== null && _a !== void 0 ? _a : getQualifiedSymbolId(deproxiedSym)) !== null && _b !== void 0 ? _b : t('errors.unknown'),
                }), {
                    path: 'Expression',
                }));
            }
            consumedMembers.push(Object.assign(Object.assign({ type: ExpressionMemberType.symbol, value: deproxiedSym }, (errors.length > 0 ? { errors } : {})), { getErrorAggregate: (scope, severity) => filterErrors(scope, severity, errors).concat(filterErrors(scope, severity, deproxiedSym === null || deproxiedSym === void 0 ? void 0 : deproxiedSym.errors)) }));
        }
        catch (e) {
            // this should not happen given dynamic proxies should always respond to property gets
            errors.push(new DominoBuilderError(DominoBuilderErrorType.InvalidSymbolReference, DominoBuilderErrorScope.DESIGN, DominoBuilderErrorSeverity.ERROR, t('errors.failed_to_find_symbol', {
                symbolName: expr.name,
            }), {
                path: 'Expression',
            }));
        }
    }
    _constructBinaryExpressionMembers(expr, scope, lineNumber, errors, consumedMembers) {
        const subExpMembers = [];
        const binaryExp = expr;
        this._constructMembers(binaryExp.left, scope, lineNumber, errors, subExpMembers, true);
        subExpMembers.push({
            type: ExpressionMemberType.operator,
            value: binaryExp.operator,
        });
        this._constructMembers(binaryExp.right, scope, lineNumber, errors, subExpMembers, true);
        const subExpression = new DominoBuilderExpression(this._expectedType, this._initialValue);
        subExpression._loadMembers(subExpMembers);
        consumedMembers.push({
            type: ExpressionMemberType.expression,
            value: subExpression,
        });
    }
    _constructCallExpressionMembers(expr, scope, lineNumber, errors, consumedMembers, isSubExpression = false) {
        const callExp = expr;
        let callee;
        if (callExp.callee.type === 'Identifier') {
            callee = callExp.callee.name;
        }
        else {
            const obj = callExp.callee.object.name;
            const prop = callExp.callee.property.name;
            callee = `${obj}.${prop}`;
        }
        const argMembers = [];
        callExp.arguments.forEach(arg => {
            this._constructMembers(arg, scope, lineNumber, errors, argMembers, true);
        });
        // wrap with an expression only if we are not already within an expression
        if (isSubExpression) {
            consumedMembers.push({
                type: ExpressionMemberType.call,
                value: callee,
                args: argMembers,
            });
        }
        else {
            const subExpression = new DominoBuilderExpression(this._expectedType, this._initialValue);
            subExpression._loadMembers([
                {
                    type: ExpressionMemberType.call,
                    value: callee,
                    args: argMembers,
                },
            ]);
            consumedMembers.push({
                type: ExpressionMemberType.expression,
                value: subExpression,
            });
        }
    }
}
//# sourceMappingURL=DominoBuilderExpression.js.map